// Popover met de twee scenario-lijsten (publiek + persoonlijk) en de
// bijbehorende acties: laden, kopiëren, verwijderen. Wordt vanuit de
// topbar geopend.

import { useEffect, useRef, useState } from "react";
import { Copy, FilePlus2, Trash2, ListPlus, Star, Settings } from "lucide-react";
import { useStore } from "../state/store";
import { Button } from "./ui/Button";
import { AdminSettingsDialog } from "./AdminSettingsDialog";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ScenarioMenu({ open, onClose }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const personalList = useStore((s) => s.personalScenarios);
  const publicList = useStore((s) => s.publicScenarios);
  const currentId = useStore((s) => s.scenario.id);
  const isAdmin = useStore((s) => s.authUser?.isAdmin === true);
  const defaultId = useStore((s) => s.defaultScenarioId);
  const setDefault = useStore((s) => s.setDefaultScenarioId);

  const newScenario = useStore((s) => s.newScenario);
  const loadScenario = useStore((s) => s.loadScenario);
  const loadPublicScenario = useStore((s) => s.loadPublicScenario);
  const duplicateAsPersonal = useStore((s) => s.duplicateAsPersonal);
  const deletePersonal = useStore((s) => s.deletePersonal);
  const deletePublic = useStore((s) => s.deletePublicScenario);
  const publishCurrent = useStore((s) => s.publishCurrentScenario);
  const refreshPersonalList = useStore((s) => s.refreshPersonalList);
  const refreshPublicList = useStore((s) => s.refreshPublicList);

  // Sluit op klik buiten + Esc (Modal heeft Esc al; hier voor de popover-mode).
  useEffect(() => {
    if (!open) return;
    refreshPersonalList();
    refreshPublicList();
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    setTimeout(() => document.addEventListener("mousedown", onDocClick), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, refreshPersonalList, refreshPublicList]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1 z-40 w-96 bg-card border border-border rounded-lg shadow-xl overflow-hidden"
    >
      <div className="p-3 border-b border-border flex items-center justify-between">
        <span className="text-sm font-semibold">Scenario's</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            newScenario();
            onClose();
          }}
        >
          <FilePlus2 size={14} /> Nieuw
        </Button>
      </div>

      <div className="max-h-[60vh] overflow-y-auto">
        <ScenarioSection
          title="Publiek"
          empty="Nog geen publieke scenario's gevonden."
        >
          {publicList.map((s) => {
            const isDefault = s.id === defaultId;
            return (
              <li
                key={s.id}
                className={
                  "flex items-center gap-1 px-2 py-1.5 rounded hover:bg-accent " +
                  (s.id === currentId ? "bg-blue-50 text-blue-900" : "")
                }
              >
                <button
                  onClick={() => {
                    loadPublicScenario(s.id);
                    onClose();
                  }}
                  className="flex-1 text-left text-sm truncate"
                >
                  <div className="truncate font-medium flex items-center gap-1">
                    {isDefault && (
                      <Star
                        size={12}
                        className="fill-amber-400 text-amber-500 flex-none"
                      />
                    )}
                    <span className="truncate">{s.name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {s.author || "(zonder auteur)"} · {fmtDate(s.updatedAt)}
                    {isDefault ? " · default" : ""}
                  </div>
                </button>
                {isAdmin && (
                  <button
                    onClick={async () => {
                      try {
                        await setDefault(isDefault ? null : s.id);
                      } catch (err) {
                        alert(
                          "Default-instelling mislukt: " +
                            (err instanceof Error ? err.message : String(err)),
                        );
                      }
                    }}
                    title={
                      isDefault
                        ? "Verwijder als default"
                        : "Markeer als default-scenario voor nieuwe bezoekers"
                    }
                    className={
                      "p-1 rounded text-muted-foreground hover:text-amber-600 hover:bg-amber-100/60 " +
                      (isDefault ? "text-amber-600" : "")
                    }
                  >
                    <Star
                      size={14}
                      className={isDefault ? "fill-amber-400" : ""}
                    />
                  </button>
                )}
                <button
                  onClick={async () => {
                    await loadPublicScenario(s.id);
                    await duplicateAsPersonal(useStore.getState().scenario);
                    onClose();
                  }}
                  title="Kopieer naar persoonlijk"
                  className="p-1 rounded hover:bg-blue-200/50 text-muted-foreground hover:text-foreground"
                >
                  <Copy size={14} />
                </button>
                {isAdmin && (
                  <button
                    onClick={async () => {
                      if (!confirm(`Publiek scenario "${s.name}" verwijderen?`)) return;
                      try {
                        await deletePublic(s.id);
                      } catch (err) {
                        alert(
                          "Verwijderen mislukt: " +
                            (err instanceof Error ? err.message : String(err)),
                        );
                      }
                    }}
                    title="Verwijder (admin)"
                    className="p-1 rounded hover:bg-rose-200/50 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ScenarioSection>

        <ScenarioSection
          title="Persoonlijk (alleen op dit apparaat)"
          empty="Geen persoonlijke scenario's. Begin een nieuw scenario of importeer er een."
        >
          {personalList.map((s) => (
            <li
              key={s.id}
              className={
                "flex items-center gap-1 px-2 py-1.5 rounded hover:bg-accent " +
                (s.id === currentId ? "bg-blue-50 text-blue-900" : "")
              }
            >
              <button
                onClick={() => {
                  loadScenario(s);
                  onClose();
                }}
                className="flex-1 text-left text-sm truncate"
              >
                <div className="truncate font-medium">{s.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {fmtDate(s.updatedAt, "datetime")}
                </div>
              </button>
              <button
                onClick={async () => {
                  if (!confirm(`Persoonlijk scenario "${s.name}" verwijderen?`)) return;
                  await deletePersonal(s.id);
                }}
                title="Verwijder"
                className="p-1 rounded hover:bg-rose-200/50 text-muted-foreground hover:text-destructive"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ScenarioSection>
      </div>

      {isAdmin && <AdminFooter onClose={onClose} publishCurrent={publishCurrent} />}
    </div>
  );
}

function AdminFooter({
  onClose,
  publishCurrent,
}: {
  onClose: () => void;
  publishCurrent: () => Promise<void>;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  return (
    <>
      <div className="p-2 border-t border-border bg-secondary/40 space-y-1.5">
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={async () => {
            try {
              await publishCurrent();
              onClose();
            } catch (err) {
              alert(
                "Publiceren mislukt: " +
                  (err instanceof Error ? err.message : String(err)),
              );
            }
          }}
        >
          <ListPlus size={14} /> Huidig scenario publiceren / bijwerken
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="w-full"
          onClick={() => setSettingsOpen(true)}
        >
          <Settings size={14} /> Site-instellingen (logo)
        </Button>
        <p className="text-[11px] text-muted-foreground leading-snug">
          Een gepubliceerd scenario is zichtbaar voor iedereen. Een bestaand
          scenario met dezelfde id wordt overschreven.
        </p>
      </div>
      <AdminSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}

function ScenarioSection({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  return (
    <section className="px-2 py-2 border-b border-border last:border-b-0">
      <h3 className="text-xs font-semibold uppercase text-muted-foreground px-1 mb-1">
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground px-1 py-1">{empty}</p>
      ) : (
        <ul>{children}</ul>
      )}
    </section>
  );
}



function fmtDate(value: unknown, mode: "date" | "datetime" = "date"): string {
  if (!value) return "—";
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return "—";
  return mode === "datetime"
    ? d.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })
    : d.toLocaleDateString("nl-NL");
}

